import { countryOf, filterByCountry, SuiteCountry } from "@/shared/country/country-scope"
import { COUNTRY } from "@/shared/constants"
import { isRiderCenterCompatible, stopCenterOf, StopWithPickings } from "./logistics-center"

type WithCountry = { country?: string | null }

/** País común de unos puntos (sin país = Perú). `null` si no hay puntos o si son de países distintos */
export const commonCountryOf = (stops: WithCountry[]): SuiteCountry | null => {
  if (!stops.length) return null
  const first = countryOf(stops[0].country)
  return stops.every((stop) => countryOf(stop.country) === first) ? first : null
}

/**
 * Despachadores a los que se les pueden asignar estos puntos: solo los de su país y de su centro (el back omite los de otro país
 * u otro centro: a un punto de Lima no se le asigna un repartidor de Trujillo). Sin puntos, todos; puntos de países distintos,
 * ninguno. Un repartidor sin centro cargado, o un punto del que no se sabe el centro, no se restringe.
 */
export const ridersForStops = <T extends WithCountry & { center?: string | null }>(riders: T[], stops: (WithCountry & StopWithPickings)[]): T[] => {
  if (!stops.length) return riders
  const country = commonCountryOf(stops)
  if (!country) return []
  return filterByCountry(riders, country).filter((rider) => stops.every((stop) => isRiderCenterCompatible(rider.center, stopCenterOf(stop))))
}

/**
 * ¿El punto es de Ecuador? Ecuador no tiene RFID ni DISET (el middleware RFID es solo de Perú): los textos de las pantallas
 * de puntos de entrega y recojo hablan solo de Odoo y de la app móvil. Perú (o sin país) conserva sus textos de siempre.
 */
export const esPuntoEcuador = (stop?: WithCountry | null): boolean => countryOf(stop?.country) === COUNTRY.ECUADOR

/**
 * ¿El correlativo es de un punto de Ecuador? ("EC-PE-…" / "EC-PR-…"; Perú: "PE-…" / "PR-…"). Un grupo de la bolsa de
 * productos pendientes no trae país: es el del punto del que salió, y de ese punto sí se conoce el correlativo.
 */
export const esCorrelativoEcuador = (correlativo?: string | null): boolean => (correlativo ?? "").trim().toUpperCase().startsWith("EC-")
